// src/components/DomainStart.js
// Champ « Tester mon site » de la page d'accueil. Aucune analyse ici : le domaine est gardé
// en mémoire, le visiteur crée son compte, puis son audit offert est pré-rempli avec ce domaine.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const PENDING_DOMAIN_KEY = 'cg_pending_domain';
const DOMAIN_SYNTAX = /^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export function cleanDomain(value) {
  return String(value || '').trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[/?#]/)[0];
}

export function readPendingDomain() {
  try { return localStorage.getItem(PENDING_DOMAIN_KEY) || ''; } catch { return ''; }
}

export function clearPendingDomain() {
  try { localStorage.removeItem(PENDING_DOMAIN_KEY); } catch { /* stockage indisponible */ }
}

const TEXT = {
  fr: { label: 'Votre site est-il bien protégé ?', placeholder: 'votre-site.fr', button: 'Tester mon site', hint: 'Créez votre compte en 30 secondes : votre premier audit est offert, sans carte bancaire.', invalid: 'Saisissez un nom de domaine, par exemple mon-entreprise.fr' },
  en: { label: 'Is your website well protected?', placeholder: 'your-site.com', button: 'Test my site', hint: 'Create your account in 30 seconds: your first audit is free, no credit card.', invalid: 'Enter a domain name, for example my-company.com' },
  es: { label: '¿Su sitio está bien protegido?', placeholder: 'su-sitio.es', button: 'Probar mi sitio', hint: 'Cree su cuenta en 30 segundos: su primera auditoría es gratis, sin tarjeta.', invalid: 'Introduzca un dominio, por ejemplo mi-empresa.es' },
};

export default function DomainStart({ lang = 'fr' }) {
  const t = TEXT[lang] || TEXT.fr;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const domain = cleanDomain(value);
    if (!DOMAIN_SYNTAX.test(domain)) { setError(t.invalid); return; }
    try { localStorage.setItem(PENDING_DOMAIN_KEY, domain); } catch { /* stockage indisponible : le visiteur ressaisira son domaine */ }
    navigate(user ? '/audits/new' : '/register');
  };

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto mt-12 text-left bg-zinc-900/80 border border-zinc-800 p-4 sm:p-5" data-testid="domain-start" noValidate>
      <label htmlFor="domain-start-input" className="block text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">{t.label}</label>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <input id="domain-start-input" value={value} onChange={(e) => { setValue(e.target.value); setError(''); }}
            placeholder={t.placeholder} autoComplete="url" inputMode="url" aria-invalid={!!error}
            className="w-full bg-black border border-zinc-700 p-3 pl-10 text-white font-mono focus:border-blue-500 outline-none placeholder:text-zinc-600" />
        </div>
        <button type="submit" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 font-mono text-sm font-bold uppercase text-white">
          {t.button} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      {error ? <p className="text-red-400 text-sm mt-3" role="alert">{error}</p> : <p className="text-zinc-500 text-xs mt-3">{t.hint}</p>}
    </form>
  );
}
